import { useState, useEffect, useCallback, useRef } from 'react';
import PropTypes from 'prop-types';
import useKeyboardNav from '@/hooks/useKeyboardNav';
import SOCControls from './SOCControls';
import ConnectionStatus from './ConnectionStatus';
import ExecutiveSummaryView from './views/ExecutiveSummaryView';
import DLPIncidentsView from './views/DLPIncidentsView';
import InsiderRiskView from './views/InsiderRiskView';
import IntuneSecurityView from './views/IntuneSecurityView';
import EntraIDSecurityView from './views/EntraIDSecurityView';
import RealTimeAlertsView from './views/RealTimeAlertsView';

/**
 * SOC Carousel Component
 * Main carousel container that cycles through 6 security monitoring views
 * 
 * @param {Object} props - Component props
 * @param {Object} props.data - Current security data
 * @param {Object} props.previousData - Previous period data for trend calculation
 * @param {Function} props.onRefresh - Callback to refresh all data
 * @param {Function} props.onExit - Callback to exit SOC mode
 * @param {number} props.cycleInterval - Auto-cycle interval in seconds (default: 15)
 * @param {string} props.connectionStatus - Connection status ('online', 'updating', 'offline')
 * @param {string} props.countdownDisplay - Countdown timer display
 * @param {Date} props.lastRefreshTime - Last refresh timestamp
 * @param {boolean} props.autoRefreshPaused - Whether auto-refresh is paused
 * @param {Function} props.toggleAutoRefreshPause - Callback to toggle auto-refresh pause
 */
const SOCCarousel = ({
  data,
  previousData,
  onRefresh,
  onExit,
  cycleInterval = 15,
  connectionStatus,
  countdownDisplay,
  lastRefreshTime,
  autoRefreshPaused
  // toggleAutoRefreshPause - intentionally not used
}) => {
  // State for current active view index (0-5)
  const [currentViewIndex, setCurrentViewIndex] = useState(0);
  
  // State for auto-cycling pause
  const [isPaused, setIsPaused] = useState(false);
  
  // State for fullscreen mode
  const [isFullscreen, setIsFullscreen] = useState(false);

  // Ref for the carousel container
  const carouselRef = useRef(null);

  // Ref for auto-cycle interval
  const cycleIntervalRef = useRef(null);

  // Define the 6 views configuration
  const views = [
    { 
      id: 'executive-summary', 
      title: 'Executive Summary', 
      component: ExecutiveSummaryView 
    },
    { 
      id: 'dlp-incidents', 
      title: 'DLP Incidents', 
      component: DLPIncidentsView 
    },
    { 
      id: 'insider-risk', 
      title: 'Insider Risk', 
      component: InsiderRiskView 
    },
    { 
      id: 'intune-security', 
      title: 'Intune Security', 
      component: IntuneSecurityView 
    },
    { 
      id: 'entra-security', 
      title: 'Entra ID Security', 
      component: EntraIDSecurityView 
    },
    { 
      id: 'real-time-alerts', 
      title: 'Real-Time Alerts', 
      component: RealTimeAlertsView 
    }
  ];

  /**
   * Navigates to the next view
   */
  const goToNext = useCallback(() => {
    setCurrentViewIndex(prev => (prev + 1) % views.length);
  }, [views.length]);

  /**
   * Navigates to the previous view
   */
  const goToPrevious = useCallback(() => {
    setCurrentViewIndex(prev => (prev - 1 + views.length) % views.length);
  }, [views.length]);

  /**
   * Toggles pause state for auto-cycling
   */
  const togglePause = useCallback(() => {
    setIsPaused(prev => !prev);
  }, []);

  /**
   * Toggles fullscreen mode
   */
  const toggleFullscreen = useCallback(() => {
    if (!document.fullscreenElement) {
      // Enter fullscreen
      if (carouselRef.current?.requestFullscreen) {
        carouselRef.current.requestFullscreen();
      } else if (carouselRef.current?.webkitRequestFullscreen) {
        carouselRef.current.webkitRequestFullscreen();
      } else if (carouselRef.current?.msRequestFullscreen) {
        carouselRef.current.msRequestFullscreen();
      }
    } else {
      // Exit fullscreen
      if (document.exitFullscreen) {
        document.exitFullscreen();
      } else if (document.webkitExitFullscreen) {
        document.webkitExitFullscreen();
      } else if (document.msExitFullscreen) {
        document.msExitFullscreen();
      }
    }
  }, []);

  /**
   * Handles fullscreen change events
   */
  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);
    document.addEventListener('webkitfullscreenchange', handleFullscreenChange);
    document.addEventListener('msfullscreenchange', handleFullscreenChange);

    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
      document.removeEventListener('webkitfullscreenchange', handleFullscreenChange);
      document.removeEventListener('msfullscreenchange', handleFullscreenChange);
    };
  }, []);

  /**
   * Sets up auto-cycling interval
   */
  useEffect(() => {
    if (isPaused) {
      // Clear interval when paused
      if (cycleIntervalRef.current) {
        clearInterval(cycleIntervalRef.current);
        cycleIntervalRef.current = null;
      }
      return;
    }

    // Start auto-cycling
    cycleIntervalRef.current = setInterval(() => {
      goToNext();
    }, cycleInterval * 1000);

    return () => {
      if (cycleIntervalRef.current) {
        clearInterval(cycleIntervalRef.current);
      }
    };
  }, [isPaused, cycleInterval, goToNext]);

  /**
   * Set up keyboard navigation
   */
  useKeyboardNav({
    onNext: goToNext,
    onPrevious: goToPrevious,
    onTogglePause: togglePause,
    onToggleFullscreen: toggleFullscreen,
    enabled: true
  });

  /**
   * Renders the current active view
   */
  const renderCurrentView = () => {
    const currentView = views[currentViewIndex];
    const ViewComponent = currentView.component;

    return (
      <ViewComponent
        data={data}
        previousData={previousData}
        onRefresh={onRefresh}
      />
    );
  };

  /**
   * Renders progress indicator dots
   */
  const renderProgressIndicator = () => {
    return (
      <div className="progress-indicator">
        {views.map((view, index) => (
          <button
            key={view.id}
            className={`progress-dot ${index === currentViewIndex ? 'active' : ''}`}
            onClick={() => setCurrentViewIndex(index)}
            aria-label={`Go to ${view.title}`}
            title={view.title}
          />
        ))}
      </div>
    );
  };

  return (
    <div 
      ref={carouselRef}
      className={`soc-carousel ${isFullscreen ? 'fullscreen' : ''}`}
    >
      {/* Header with Connection Status */}
      <div className="soc-carousel-header">
        <div className="soc-title-section">
          <h1 className="soc-main-title">Security Operations Center</h1>
          <p className="soc-subtitle">{views[currentViewIndex].title}</p>
        </div>
        
        <ConnectionStatus
          status={connectionStatus}
          lastRefreshTime={lastRefreshTime}
          countdownDisplay={countdownDisplay}
          isPaused={autoRefreshPaused}
        />
        
        {onExit && (
          <button
            className="exit-soc-mode-btn"
            onClick={onExit}
            aria-label="Exit SOC Dashboard mode"
            title="Exit SOC Dashboard mode"
          >
            ✕ Exit
          </button>
        )}
      </div>

      {/* Main View Container */}
      <div className="soc-carousel-body">
        <div className="soc-view-container">
          {renderCurrentView()}
        </div>
      </div>

      {/* Footer with Controls and Progress */}
      <div className="soc-carousel-footer">
        {renderProgressIndicator()}
        
        <SOCControls
          onPrevious={goToPrevious}
          onNext={goToNext}
          onTogglePause={togglePause}
          onRefresh={onRefresh}
          onToggleFullscreen={toggleFullscreen}
          isPaused={isPaused}
          isFullscreen={isFullscreen}
        />
      </div>

      {/* Auto-Cycle Status Indicator */}
      {!isPaused && (
        <div className="auto-cycle-indicator">
          <span className="cycle-icon">⟳</span>
          <span className="cycle-text">
            Auto-cycling every {cycleInterval}s
          </span>
        </div>
      )}
    </div>
  );
};

SOCCarousel.propTypes = {
  data: PropTypes.object,
  previousData: PropTypes.object,
  onRefresh: PropTypes.func.isRequired,
  onExit: PropTypes.func,
  cycleInterval: PropTypes.number,
  connectionStatus: PropTypes.oneOf(['online', 'updating', 'offline']).isRequired,
  countdownDisplay: PropTypes.string.isRequired,
  lastRefreshTime: PropTypes.instanceOf(Date),
  autoRefreshPaused: PropTypes.bool.isRequired,
  toggleAutoRefreshPause: PropTypes.func.isRequired
};

SOCCarousel.defaultProps = {
  data: null,
  previousData: null,
  onExit: null,
  cycleInterval: 15,
  lastRefreshTime: null
};

export default SOCCarousel;
